import { Modal, Typography } from "@drone-js/core";
import { useMutation } from "@tanstack/react-query";
import { oneOnboardingApi } from "libs/api";
import type {
    DeletePhotoPictureTypeIdEnum,
    OneOnboardingPhotoPictureTypeIdEnum,
} from "libs/codegen/property-onboarding";
import type { FunctionComponent } from "react";
import { NotificationType } from "../../notification/constants";
import { useNotificationHandler } from "../../notification/useNotificationHandler";
import { toOnboardingApiIdentity } from "../../routes/onboardingRouteApi";
import { useStaticDataAtomValue } from "../../staticDataAtom";
import { useOnboardingRouteId } from "../../useOnboardingRouteId";

const noOnboardingPhotoId = "00000000-0000-0000-0000-000000000000";

interface Props {
    photoId: number;
    onboardingPhotoId?: string | null;
    pictureTypeId: OneOnboardingPhotoPictureTypeIdEnum;
    show: boolean;
    onCancel: () => void;
    onRemove: () => void;
}

export const RemovePhotoModal: FunctionComponent<Props> = ({
    photoId,
    onboardingPhotoId,
    pictureTypeId,
    show,
    onCancel,
    onRemove,
}) => {
    const route = useOnboardingRouteId();
    const apiIdentity = toOnboardingApiIdentity(route);
    const {
        cmsData: {
            photo: { cancel, delete: del, deletePhoto, areYouSureYouWantToDeleteThisPhoto },
        },
    } = useStaticDataAtomValue();
    const { addNotification } = useNotificationHandler();
    const { mutate, isLoading } = useMutation({
        mutationFn: (id: number) =>
            oneOnboardingApi
                .deletePhoto(
                    route.lang,
                    id,
                    apiIdentity.propertyId,
                    pictureTypeId as unknown as DeletePhotoPictureTypeIdEnum,
                    onboardingPhotoId ?? noOnboardingPhotoId,
                    apiIdentity.onboardingId,
                )
                .then((res) => {
                    if (res.data) return res.data;
                    else throw new Error("Failed to delete photo");
                }),
        onSuccess: () => {
            addNotification(NotificationType.DeleteSuccess);
            onRemove();
        },
        onError: () => {
            addNotification(NotificationType.DeleteFailed);
            onCancel();
        },
    });

    return (
        <Modal
            visible={show}
            title={deletePhoto}
            actions={[
                {
                    label: del,
                    hierarchy: "primary",
                    actionType: "button",
                    variant: "label",
                    state: isLoading ? "loading" : "enabled",
                    onClick: () => mutate(photoId),
                    "data-element-name": "confirm-delete-photo",
                },
                {
                    label: cancel,
                    hierarchy: "secondary",
                    actionType: "button",
                    variant: "label",
                    state: isLoading ? "disabled" : "enabled",
                    onClick: onCancel,
                },
            ]}
            onClose={onCancel}
        >
            <Typography variant="body-s">{areYouSureYouWantToDeleteThisPhoto}</Typography>
        </Modal>
    );
};
